import { Link } from "react-router-dom"
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react"
import { Card, CardHeader, CardContent, CardTitle } from "./Card"
import { Badge } from "./Badge"
import { SpeakerPortrait, PresentationVideo, PresentationThumbnail } from "./EventMedia"
import { presentationRoute } from "../data/vizag2026"

export function Presenters({ session, large = false }) {
  return (
    <ul className={`flex flex-wrap ${large ? "gap-6" : "gap-4"}`} aria-label="Presenters">
      {session.presenters.map((speaker) => (
        <li key={speaker.name} className="flex min-w-0 items-center gap-3">
          <SpeakerPortrait speaker={speaker} className={large ? "h-20 w-20 text-2xl" : "h-12 w-12 text-sm"} />
          <div>
            <p className="font-medium text-green-800">{speaker.name}</p>
            {session.speakerType === "Guest Speaker" && <Badge className="mt-1 border border-amber-200 bg-amber-50 text-amber-800">Guest Speaker</Badge>}
          </div>
        </li>
      ))}
    </ul>
  )
}

export function VizagPresentations({ sessions, event, preview = false, onShowAll }) {
  return (
    <section id="sessions" className="scroll-mt-24 bg-white py-16 sm:py-20" aria-labelledby="presentations-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-green-600">{event.shortName}</p>
          <h2 id="presentations-heading" tabIndex={-1} className="text-3xl font-bold text-green-800 sm:text-4xl">{preview ? "Presentation Preview" : "All Presentations"}</h2>
        </div>
        <div id="presentation-grid" className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {sessions.map((session) => (
            <article key={session.id} className="min-w-0">
              <Card className="group flex h-full flex-col border-green-100 transition-shadow hover:shadow-lg">
                <CardHeader>
                  <PresentationThumbnail session={session} />
                  <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-green-600">Presentation {String(session.number).padStart(2, "0")}</p>
                  <CardTitle className="mb-5 text-xl leading-snug text-green-800">
                    <Link to={presentationRoute(session)} className="hover:text-green-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-600">{session.title}</Link>
                  </CardTitle>
                  <Presenters session={session} />
                </CardHeader>
                <CardContent className="mt-auto">
                  {session.abstract && <p className="mb-5 mt-2 line-clamp-3 leading-relaxed text-green-700">{session.abstract}</p>}
                  <Link to={presentationRoute(session)} aria-label={`View presentation: ${session.title}`} className="mt-4 inline-flex items-center gap-2 font-medium text-green-700 hover:text-green-900">View Presentation <ArrowRight className="h-4 w-4" /></Link>
                </CardContent>
              </Card>
            </article>
          ))}
        </div>
        {preview && <div className="mt-10 text-center"><button type="button" onClick={onShowAll} aria-controls="presentation-grid" aria-expanded={false} className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-semibold text-white hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-700 focus-visible:outline-offset-2">View all {event.presentationCount} presentations <ArrowRight className="h-4 w-4" /></button></div>}
      </div>
    </section>
  )
}

export function VizagPresentationDetail({ session, event }) {
  return (
    <>
      <section className="bg-gradient-to-br from-green-100 to-white py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <Link to={`${event.route}#sessions`} className="mb-8 inline-flex items-center gap-2 font-medium text-green-700 hover:text-green-900"><ArrowLeft className="h-4 w-4" />Back to CTS 2026 Vizag presentations</Link>
          <p className="mb-4 text-sm font-semibold text-green-700">{event.shortName} · Presentation {String(session.number).padStart(2, "0")}</p>
          <h1 className="mb-8 text-3xl font-bold leading-tight text-green-800 sm:text-5xl">{session.title}</h1>
          <Presenters session={session} large />
          <p className="mt-8 text-sm text-green-700"><time dateTime={event.isoDate}>{event.date}</time> | <a href={event.venueUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-green-900">{event.venue}</a></p>
        </div>
      </section>
      <section className="mx-auto max-w-4xl space-y-8 px-4 py-12 sm:px-6 sm:py-16">
        {session.abstract && (
          <Card className="border-green-100">
            <CardHeader><h2 className="flex items-center gap-2 text-xl font-semibold text-green-800"><BookOpen className="h-5 w-5" />Presentation Abstract</h2></CardHeader>
            <CardContent><p className="whitespace-pre-line text-lg leading-relaxed text-green-700">{session.abstract}</p></CardContent>
          </Card>
        )}
        <Card className="border-green-100">
          <CardHeader><h2 className="text-xl font-semibold text-green-800">Presentation Video</h2></CardHeader>
          <CardContent><PresentationVideo key={session.id} session={session} /></CardContent>
        </Card>
        <Link to={`${event.route}#sessions`} className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-semibold text-white hover:bg-green-700">View all {event.presentationCount} presentations <ArrowRight className="h-4 w-4" /></Link>
      </section>
    </>
  )
}
